import type { ReactNode } from 'react';
import React, { createContext, useCallback, useContext, useState } from 'react';

import { useChatDocumentContext } from './ChatDocumentContext.tsx';

import {
  ChatContextType,
  ChatExchange,
  ChatListItem,
  ChatListResponse,
  ChatSession,
  CreateChatExchangeParams,
  CreateChatExchangeRequest,
  createChatExchangeResponse,
  CreateChatResponse,
} from '~/components/Chat/types.ts';
import { useRealTimeUpdatesChat } from '~/context/RealTimeUpdatesChatContext';
import { get, post } from '~/utils/request';

const CHAT_REQUEST_TIMEOUT = 3 * 60 * 1000; // 3 minutes
const ChatContext = createContext<ChatContextType | null>(null);

export const useClaimChatsContext = () => {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChatContext must be used within a ChatProvider');
  return ctx;
};

interface ChatProviderProps {
  children: ReactNode;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const [chatTitle, setChatTitle] = useState<string | null>(null);
  const [greeting, setGreeting] = useState<string | null>(null);
  const [exchanges, setExchanges] = React.useState<ChatExchange[]>([]);
  const [chats, setChats] = useState<ChatListItem[]>([]);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [isChatsLoading, setIsChatsLoading] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);
  const [chatsError, setChatsError] = useState<string | null>(null);

  const { reset } = useChatDocumentContext();
  const { connect: connectRealtime, registerExchangeIds } = useRealTimeUpdatesChat();

  // Auto-register exchange IDs whenever exchanges change
  React.useEffect(() => {
    const exchangeIds = exchanges.map((e) => e.id).filter((id) => id !== 'temporary-exchange-id');
    if (exchangeIds.length > 0) {
      registerExchangeIds(exchangeIds);
    }
  }, [exchanges, registerExchangeIds]);

  const resetExchanges = useCallback(() => {
    setExchanges([]);
    setChatTitle(null);
    setGreeting(null);
    setChatError(null);
    reset();
  }, [reset]);

  const fetchChats = useCallback(async (claimNumber: string) => {
    setIsChatsLoading(true);
    setChatsError(null);
    try {
      const response = await get<ChatListResponse>({
        url: `/api/v1/chats/${claimNumber}/all`,
      });
      setChats(response.chats);
    } catch (error) {
      setChatsError('Failed to fetch claim chats. Please try to refresh the page.');
      console.error('Error fetching chats:', error);
    } finally {
      setIsChatsLoading(false);
    }
  }, []);

  const getChatExchanges = useCallback(
    async (claimNumber: string, chatId?: string) => {
      setIsChatLoading(true);
      setChatError(null);
      try {
        const url = `/api/v1/chats/${claimNumber}`;
        const response = await get<ChatSession>({ url, params: { chat_id: chatId } });

        setChatTitle(response.chat_title);
        setGreeting(response.greeting);
        setExchanges(response.exchanges || []);

        // Connect to Firestore if we have a realtime token
        if (response.realtime_token) {
          await connectRealtime(response.realtime_token);
          console.log('[ChatContext] ✅ Connected to Firestore for existing chat');
        } else {
          console.log('[ChatContext] ⚠️ No realtime_token in get chat response');
        }
      } catch (error) {
        setChatError('Failed to fetch chat exchanges. Please try to refresh the page.');
        console.error('Error fetching chat exchanges:', error);
      } finally {
        setIsChatLoading(false);
      }
    },
    [connectRealtime]
  );

  const createTempExchange = (
    userInput: string,
    contextDocumentIds: string[] = [],
    documentUsedIds: string[] = []
  ): ChatExchange => ({
    id: 'temporary-exchange-id',
    created_at: new Date().toISOString(),
    user_input: userInput,
    response: '',
    documents_used: documentUsedIds,
    context_document_source_ids: contextDocumentIds,
  });

  /**
   * Creates a new chat session and establishes realtime connection
   */
  const createNewChatSession = useCallback(
    async (claimNumber: string): Promise<string> => {
      const createChatUrl = `/api/v1/chats/${claimNumber}`;
      const createResponse = await post<CreateChatResponse>({ url: createChatUrl });

      console.log(`[ChatContext] ✅ Chat created: ${createResponse.chat_id} ${createResponse.chat_title}`);

      // Update chat metadata
      setChatTitle(createResponse.chat_title);
      setGreeting(createResponse.greeting);

      // Establish Firestore connection if token is available
      if (createResponse.realtime_token) {
        console.log(
          `[ChatContext] 📡 Realtime token received from chat creation, Token expires at: ${createResponse.realtime_token.expires_at}`
        );

        try {
          await connectRealtime(createResponse.realtime_token);
          console.log('[ChatContext] ✅ Connected to Firestore successfully');
        } catch (error) {
          console.error('[ChatContext] ❌ Failed to connect to Firestore:', error);
        }
      } else {
        console.log('[ChatContext] ⚠️ No realtime_token in chat creation response');
      }

      return createResponse.chat_id;
    },
    [connectRealtime]
  );

  /**
   * Adds a temporary exchange to UI for immediate user feedback
   */
  const showTemporaryExchange = useCallback((userInput: string, usedDocuments: string[]) => {
    const tempExchange = createTempExchange(userInput, usedDocuments);
    setExchanges((prev) => [...prev, tempExchange]);
  }, []);

  /**
   * Adds a temporary exchange to UI for prompt-based input (uses prompt title)
   */
  const showTemporaryPromptExchange = useCallback((promptTitle: string, usedDocuments: string[]) => {
    const tempExchange = createTempExchange(promptTitle, usedDocuments);
    setExchanges((prev) => [...prev, tempExchange]);
  }, []);

  /**
   * Sends exchange creation request to API and updates exchanges
   */
  const sendExchangeRequest = useCallback(
    async (claimNumber: string, chatId: string, usedDocuments: string[], userInput?: string, promptKey?: string) => {
      const exchangeData: CreateChatExchangeRequest = {
        chat_id: chatId || null,
        claim_number: claimNumber,
        user_input: userInput,
        prompt_key: promptKey,
        context_document_source_ids: usedDocuments,
      };

      const url = `/api/v1/chats/${claimNumber}/exchanges`;
      const response = await post<createChatExchangeResponse>({
        url,
        data: exchangeData,
        timeout: CHAT_REQUEST_TIMEOUT,
      });

      setExchanges(response.exchanges || []);
      return response.chat_id;
    },
    []
  );

  /**
   * Creates a chat exchange (creates new chat if needed, then adds exchange)
   */
  const createChatExchange = useCallback(
    async (params: CreateChatExchangeParams) => {
      const { claimNumber, userInput, promptKey, promptTitle, chatId, usedDocuments = [] } = params;
      setIsChatLoading(true);
      setChatError(null);

      try {
        const actualChatId = chatId || (await createNewChatSession(claimNumber));

        // Show temporary exchange based on input type
        if (promptKey && promptTitle) {
          // For prompts, show the prompt_title in the temporary exchange
          showTemporaryPromptExchange(promptTitle, usedDocuments);
        } else if (userInput) {
          showTemporaryExchange(userInput, usedDocuments);
        }

        return await sendExchangeRequest(claimNumber, actualChatId, usedDocuments, userInput, promptKey);
      } catch (error) {
        setChatError('Failed to create chat exchange. Please try again.');
        console.error('Error creating chat exchange:', error);
      } finally {
        setIsChatLoading(false);
      }
    },
    [createNewChatSession, showTemporaryExchange, showTemporaryPromptExchange, sendExchangeRequest]
  );

  return (
    <ChatContext.Provider
      value={{
        greeting,
        chatTitle,
        isChatLoading,
        isChatsLoading,
        chatError,
        chatsError,
        getChatExchanges,
        createChatExchange,
        exchanges,
        chats,
        resetExchanges,
        fetchChats,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};
